export interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  /** Used to render a small visual hint in the picker before any real preview renders. */
  layout: "logo-left-right" | "logo-centered" | "minimal" | "listing";
  html: string;
  /** Loaded into the Designer's sample data when the template is picked, so the preview shows a
   *  filled-in report immediately instead of empty cells. */
  sampleData?: unknown;
}

const SHARED_TABLE_STYLES = `
      @page { margin: 16mm 14mm 18mm; @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 8.5px; color: #8a8f98; } }
      table.data-table { width: 100%; border-collapse: collapse; margin-top: 14px; }
      table.data-table th, table.data-table td { border-bottom: 1px solid #e3e6ec; padding: 7px 9px; font-size: 11px; text-align: left; }
      table.data-table th { background: {{theme.primaryColor}}; color: #fff; font-weight: 700; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: none; }
      table.data-table tr:nth-child(even) td { background: #f6f8fb; }
      table.data-table td.number-cell, table.data-table th.number-cell { text-align: right; }
      table.data-table tr.total-row td { font-weight: 700; border-top: 2px solid {{theme.primaryColor}}; background: #fff; }
      .section-title { border-left: 4px solid {{theme.primaryColor}}; padding: 4px 10px; font-weight: 700; margin: 20px 0 6px; font-size: 12.5px; color: #1f2937; background: #f6f8fb; }
      .summary-box { border: 1px solid #e3e6ec; border-left: 4px solid {{theme.primaryColor}}; background: #fafbfd; padding: 10px 14px; margin-bottom: 10px; font-size: 11px; }
      .summary-box strong { color: {{theme.primaryColor}}; }`;

export interface DesignerSnippet {
  id: string;
  name: string;
  description: string;
  html: string;
}

/** Insertable at the cursor (see the Designer's "Snippets" panel) - unlike a starter template,
 *  each of these drops one self-contained block into an EXISTING template rather than replacing
 *  it, so you can mix and match (e.g. add a logo header to a template that started blank). Every
 *  snippet uses inline styles rather than a shared CSS class, since it has to look right no matter
 *  what template it lands in - it can't assume classes like ".data-table" are already defined. */

// --- Statement building blocks -------------------------------------------------------------------
// Broken down from a classic Thymeleaf benefit statement (title, banner, member details grid,
// section heading, grouped-header summary matrix) and translated to Handlebars:
//   th:text="${a.b}"        -> {{a.b}}
//   th:text="${x ?: '0.00'}"-> {{currency x default="0.00"}}
//   .toUpperCase()          -> {{uppercase x}}
//   th:each row over rows   -> {{#each rows}} ... {{this.field}} ... {{/each}} (one loop, not one
//                              hand-written <tr> per line)
//   colorService colours    -> {{theme.primaryColor}} / {{theme.secondaryColor}}
//   th:replace header/footer-> the "Logo header" and "Footer" snippets
const P = "{{theme.primaryColor}}";
const S = "{{theme.secondaryColor}}";
const LBL = "border: 1px solid #c8d8e8; padding: 4px 5px; background: #eef3f9; font-weight: bold;";
const VAL = "border: 1px solid #c8d8e8; padding: 4px 5px; overflow-wrap: break-word;";
const HDR = `background: ${P}; color: #fff; font-weight: bold; text-align: center; padding: 4px;`;
const SUBHDR = `background: ${S}; color: #fff; font-weight: bold; text-align: center; padding: 4px; font-size: 8px;`;

const STATEMENT_BANNER = `<!-- Statement title + banner -->
<div style="text-align: center; font-size: 14px; font-weight: bold; letter-spacing: 0.5px; text-transform: uppercase; margin-bottom: 4px;">
  {{#if sacco.name}}{{sacco.name}}{{else}}{{theme.companyName}}{{/if}}
</div>
<div style="background: ${P}; color: #fff; text-align: center; font-weight: bold; font-size: 11px; padding: 5px 0; letter-spacing: 0.4px; margin-bottom: 8px;">
  MEMBER STATEMENT AS AT {{uppercase (dateFormat params.dateTo "dd MMM yyyy")}}
</div>
`;

const MEMBER_DETAILS_GRID = `<!-- Member details: label / value pairs, three per row -->
<table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 9px; margin-bottom: 8px;">
  <colgroup><col style="width: 12%;"/><col style="width: 21%;"/><col style="width: 12%;"/><col style="width: 21%;"/><col style="width: 12%;"/><col style="width: 22%;"/></colgroup>
  <tr><td colspan="6" style="background: ${P}; color: #fff; padding: 5px 6px; font-size: 10px; font-weight: bold;">MEMBER PERSONAL DETAILS</td></tr>
  <tr>
    <td style="${LBL}">Member name</td><td colspan="3" style="${VAL}">{{member.fullName}}</td>
    <td style="${LBL}">Date joined</td><td style="${VAL}">{{dateFormat member.dateJoined "dd MMM yyyy"}}</td>
  </tr>
  <tr>
    <td style="${LBL}">Member no.</td><td style="${VAL}">{{member.memberNo}}</td>
    <td style="${LBL}">ID number</td><td style="${VAL}">{{member.idNo}}</td>
    <td style="${LBL}">Phone</td><td style="${VAL}">{{member.phone}}</td>
  </tr>
  <tr>
    <td style="${LBL}">Date of birth</td><td style="${VAL}">{{dateFormat member.dob "dd MMM yyyy"}}</td>
    <td style="${LBL}">KRA PIN</td><td style="${VAL}">{{member.kraPin}}</td>
    <td style="${LBL}">Email</td><td style="${VAL}">{{member.email}}</td>
  </tr>
  <tr>
    <td style="${LBL}">Branch</td><td style="${VAL}">{{member.branch}}</td>
    <td style="${LBL}">Payroll no.</td><td style="${VAL}">{{member.payrollNo}}</td>
    <td style="${LBL}">Status</td><td style="${VAL}">{{member.status}}</td>
  </tr>
</table>
`;

const SECTION_HEADING = `<!-- Section heading + certificate paragraph -->
<div style="font-size: 11px; font-weight: bold; color: ${P}; margin: 8px 0 2px; border-left: 4px solid ${P}; padding-left: 6px;">ACCOUNT SUMMARY</div>
<p style="font-size: 8.5px; color: #333; line-height: 1.4; margin: 0 0 5px;">
  This is to certify that <strong>{{member.fullName}}</strong> (Member No. {{member.memberNo}}) is a member of
  <strong>{{#if sacco.name}}{{sacco.name}}{{else}}{{theme.companyName}}{{/if}}</strong>. The balances standing to the
  credit of the member's accounts, from inception up to and including {{dateFormat params.dateTo "dd MMM yyyy"}}, are as follows:
</p>
`;

const CELL = "border: 1px solid #b0c8de; padding: 4px;";
const SUMMARY_MATRIX = `<!-- Summary matrix: two account groups (deposits + shares). Map a list to summary.rows on the Field
     mapping tab - one object per line: { label, deposits, holiday, depositsTotal, shares, bonus,
     sharesTotal, closing }. Set closing to true on the row that should be bold (closing balance).
     For striped rows add this to your template's <head> <style>:
     .rf-matrix tbody tr:nth-child(even) td { background: #f6f8fb; } -->
<table class="rf-matrix" style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 8.5px;">
  <colgroup><col style="width: 22%;"/><col style="width: 13%;"/><col style="width: 13%;"/><col style="width: 13%;"/><col style="width: 13%;"/><col style="width: 13%;"/><col style="width: 13%;"/></colgroup>
  <thead>
    <tr>
      <th rowspan="2" style="${HDR} border: 1px solid ${P}; text-align: left;">Summary details</th>
      <th colspan="3" style="${HDR} border: 1px solid ${P};">MEMBER DEPOSITS ({{#if sacco.currency}}{{sacco.currency}}{{else}}KES{{/if}})</th>
      <th colspan="3" style="${HDR} border: 1px solid ${P};">SHARE CAPITAL ({{#if sacco.currency}}{{sacco.currency}}{{else}}KES{{/if}})</th>
    </tr>
    <tr>
      <th style="${SUBHDR} border: 1px solid ${S};">Ordinary deposits</th><th style="${SUBHDR} border: 1px solid ${S};">Holiday savings</th><th style="${SUBHDR} border: 1px solid ${S};">Total</th>
      <th style="${SUBHDR} border: 1px solid ${S};">Paid-up shares</th><th style="${SUBHDR} border: 1px solid ${S};">Bonus shares</th><th style="${SUBHDR} border: 1px solid ${S};">Total</th>
    </tr>
  </thead>
  <tbody>
    {{#each summary.rows}}
    <tr>
      <td style="${CELL} font-weight: 600;{{#if this.closing}} border-top: 2px solid ${P}; font-weight: bold;{{/if}}">{{this.label}}</td>
      <td style="${CELL} text-align: right; white-space: nowrap;{{#if this.closing}} border-top: 2px solid ${P}; font-weight: bold;{{/if}}">{{currency this.deposits default="0.00"}}</td>
      <td style="${CELL} text-align: right; white-space: nowrap;{{#if this.closing}} border-top: 2px solid ${P}; font-weight: bold;{{/if}}">{{currency this.holiday default="0.00"}}</td>
      <td style="${CELL} text-align: right; white-space: nowrap; font-weight: bold; background: #eef3f9;{{#if this.closing}} border-top: 2px solid ${P};{{/if}}">{{currency this.depositsTotal default="0.00"}}</td>
      <td style="${CELL} text-align: right; white-space: nowrap;{{#if this.closing}} border-top: 2px solid ${P}; font-weight: bold;{{/if}}">{{currency this.shares default="0.00"}}</td>
      <td style="${CELL} text-align: right; white-space: nowrap;{{#if this.closing}} border-top: 2px solid ${P}; font-weight: bold;{{/if}}">{{currency this.bonus default="0.00"}}</td>
      <td style="${CELL} text-align: right; white-space: nowrap; font-weight: bold; background: #eef3f9;{{#if this.closing}} border-top: 2px solid ${P};{{/if}}">{{currency this.sharesTotal default="0.00"}}</td>
    </tr>
    {{/each}}
    <tr>
      <td style="background: ${P}; color: #fff; font-weight: bold; border: 1px solid ${P}; padding: 4px;">GRAND TOTAL</td>
      <td colspan="6" style="background: ${P}; color: #fff; font-weight: bold; text-align: right; font-size: 9.5px; border: 1px solid ${P}; padding: 4px;">{{currency summary.grandTotal default="0.00"}}</td>
    </tr>
  </tbody>
</table>
`;

export const SNIPPETS: DesignerSnippet[] = [
  {
    id: "logo-header-split",
    name: "Logo header — left & right",
    description: "Logo on the left, company name/contact centered — drop at the top of a template.",
    html: `<!-- Logo header -->
<table style="width: 100%; border-collapse: collapse; margin-bottom: 12px;">
  <tr>
    <td style="width: 20%; vertical-align: middle; padding: 4px;">
      {{#if theme.logoUrl}}<img src="{{theme.logoUrl}}" alt="Logo" style="max-width: 90px; max-height: 90px; object-fit: contain;" />{{/if}}
    </td>
    <td style="width: 60%; text-align: center; vertical-align: middle; padding: 4px;">
      <div style="font-size: 16px; font-weight: 700; color: {{theme.primaryColor}}; margin: 0 0 4px;">{{theme.companyName}}</div>
      <div style="font-size: 10.5px; color: #444; line-height: 1.5;">
        {{#if theme.email}}Email: {{theme.email}}{{/if}}{{#if theme.phone}} &nbsp;|&nbsp; Tel: {{theme.phone}}{{/if}}<br />
        {{#if theme.address}}{{theme.address}}{{/if}}
      </div>
    </td>
    <td style="width: 20%;"></td>
  </tr>
</table>
`,
  },
  {
    id: "logo-header-centered",
    name: "Logo header — centered",
    description: "A single centered logo above the company name and contact details.",
    html: `<!-- Logo header -->
<div style="text-align: center; margin-bottom: 14px;">
  {{#if theme.logoUrl}}<img src="{{theme.logoUrl}}" alt="Logo" style="max-width: 130px; max-height: 90px; object-fit: contain; margin-bottom: 6px;" /><br />{{/if}}
  <div style="font-size: 17px; font-weight: 700; color: {{theme.primaryColor}}; margin: 2px 0;">{{theme.companyName}}</div>
  <div style="font-size: 10.5px; color: #444; line-height: 1.6;">
    {{#if theme.address}}{{theme.address}}<br />{{/if}}
    {{#if theme.email}}Email: {{theme.email}}{{/if}}{{#if theme.phone}} &nbsp;|&nbsp; Tel: {{theme.phone}}{{/if}}
  </div>
</div>
`,
  },
  {
    id: "footer-block",
    name: "Footer — printed by / company details",
    description: "Who printed it and when, plus company contact details — drop near the end of a template.",
    html: `<!-- Footer -->
<table style="width: 100%; border-collapse: collapse; margin-top: 24px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 9.5px; color: #555;">
  <tr>
    <td style="width: 50%; vertical-align: top; padding: 4px;">
      Printed by: {{params.preparedBy}}<br />
      Date printed: {{params.printedAt}}
    </td>
    <td style="width: 50%; text-align: right; vertical-align: top; padding: 4px;">
      {{theme.companyName}}<br />
      {{theme.address}}<br />
      {{#if theme.email}}{{theme.email}}{{/if}}{{#if theme.phone}} &nbsp;|&nbsp; {{theme.phone}}{{/if}}
    </td>
  </tr>
</table>
`,
  },
  {
    id: "list-table",
    name: "List table",
    description: "A table that loops over a mapped array variable (see the Field mapping tab's \"Ready-to-paste list template\" for one built from your real data).",
    // Coming from Thymeleaf: th:each="x : ${list}" -> {{#each list}}...{{/each}} (current item is
    // "this"); th:text="${x.field}" -> {{this.field}}; th:class odd/even row striping -> not
    // needed, ":nth-child(even)" below stripes rows automatically.
    html: `<!-- List table: replace "items" with your mapped array variable name from Field mapping -->
<table style="width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 11px;">
  <thead>
    <tr style="background: {{theme.primaryColor}}; color: #fff;">
      <th style="padding: 6px 8px; text-align: left;">Date</th>
      <th style="padding: 6px 8px; text-align: left;">Description</th>
      <th style="padding: 6px 8px; text-align: right;">Amount</th>
    </tr>
  </thead>
  <tbody>
    {{#each items}}
    <tr>
      <td style="border: 1px solid #ddd; padding: 6px 8px;">{{dateFormat this.date "dd/MM/yyyy"}}</td>
      <td style="border: 1px solid #ddd; padding: 6px 8px;">{{this.description}}</td>
      <td style="border: 1px solid #ddd; padding: 6px 8px; text-align: right;">{{currency this.amount}}</td>
    </tr>
    {{/each}}
  </tbody>
</table>
`,
  },
  {
    id: "statement-banner",
    name: "Statement title + banner",
    description: "SACCO name and a coloured \"MEMBER STATEMENT AS AT <date>\" banner.",
    html: STATEMENT_BANNER,
  },
  {
    id: "member-details-grid",
    name: "Member details grid",
    description: "Label/value grid: name, member no., ID, phone, KRA PIN, branch, status - reads from a member object.",
    html: MEMBER_DETAILS_GRID,
  },
  {
    id: "section-heading",
    name: "Section heading + certificate text",
    description: "An accented section title with a short certificate paragraph under it.",
    html: SECTION_HEADING,
  },
  {
    id: "summary-matrix",
    name: "Summary matrix (grouped headers)",
    description: "Two-level header table (deposits + shares) driven by one {{#each summary.rows}} loop, with a closing-balance row and grand total.",
    html: SUMMARY_MATRIX,
  },
];


const snippetHtml = (id: string) => SNIPPETS.find((sn) => sn.id === id)!.html;

/** Matches the variables the SACCO statement snippets read, so the picker shows a filled-in page. */
const SACCO_SAMPLE_DATA = {
  report: { displayName: "Member Statement" },
  params: { dateFrom: "2026-01-01", dateTo: "2026-12-31", preparedBy: "admin", printedAt: "19 Sep 2026" },
  sacco: { name: "Mukumu SACCO Society Limited", currency: "KES" },
  member: {
    fullName: "Wanjiku Mwangi", memberNo: "M-004512", idNo: "24567890", phone: "0712 345 678",
    dob: "1986-03-14", kraPin: "A012345678Z", email: "wanjiku@example.com", branch: "Kakamega",
    payrollNo: "PR-2210", status: "Active", dateJoined: "2018-06-01",
  },
  summary: {
    rows: [
      { label: "Opening balance as at 01 Jan 2026", deposits: 152300, holiday: 24000, depositsTotal: 176300, shares: 50000, bonus: 8200, sharesTotal: 58200 },
      { label: "Contributions", deposits: 42000, holiday: 12000, depositsTotal: 54000, shares: 6000, bonus: 0, sharesTotal: 6000 },
      { label: "Transfers in", deposits: 0, holiday: 0, depositsTotal: 0, shares: 0, bonus: 0, sharesTotal: 0 },
      { label: "Withdrawals", deposits: -10000, holiday: 0, depositsTotal: -10000, shares: 0, bonus: 0, sharesTotal: 0 },
      { label: "Dividends & interest", deposits: 9138, holiday: 1440, depositsTotal: 10578, shares: 3500, bonus: 574, sharesTotal: 4074 },
      { label: "Closing balance as at 31 Dec 2026", deposits: 193438, holiday: 37440, depositsTotal: 230878, shares: 59500, bonus: 8774, sharesTotal: 68274, closing: true },
    ],
    grandTotal: 299152,
  },
};

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: "executive",
    name: "Executive statement — branded band",
    description: "Full-width brand-colour header, summary cards, a striped data table with a totals row and page numbers. The most polished starting point.",
    layout: "logo-left-right",
    html: `<!doctype html>
<html>
  <head>
    <style>
      @page { margin: 0 0 16mm; @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 8.5px; color: #8a8f98; margin-right: 14mm; } }
      body { font-family: "Calibri", Arial, sans-serif; font-size: 12px; color: #1f2937; margin: 0; }
      .band { background: {{theme.primaryColor}}; color: #fff; padding: 18px 14mm; }
      .band table { width: 100%; border-collapse: collapse; }
      .band td { vertical-align: middle; }
      .band img { max-height: 54px; max-width: 120px; object-fit: contain; }
      .band .company { font-size: 18px; font-weight: 700; letter-spacing: 0.02em; }
      .band .contact { font-size: 9.5px; opacity: 0.92; line-height: 1.5; text-align: right; }
      .accent { height: 4px; background: {{theme.accentColor}}; }
      .content { padding: 16px 14mm 0; }
      .title { font-size: 20px; font-weight: 700; color: #111827; margin: 0; }
      .subtitle { font-size: 11px; color: #6b7280; margin: 2px 0 14px; }
      table.cards { width: 100%; border-collapse: collapse; margin: 0 0 6px; }
      table.cards td { width: 33%; background: #f6f8fb; border-top: 3px solid {{theme.primaryColor}}; border-bottom: 1px solid #e3e6ec; padding: 9px 12px; vertical-align: top; }
      table.cards td.gap { border-left: 8px solid #fff; }
      .card-label { font-size: 8.5px; text-transform: uppercase; letter-spacing: 0.08em; color: #6b7280; font-weight: 700; }
      .card-value { font-size: 13px; font-weight: 700; color: #111827; margin-top: 3px; }
      .section-title { border-left: 4px solid {{theme.primaryColor}}; padding: 4px 10px; font-weight: 700; margin: 20px 0 6px; font-size: 12.5px; background: #f6f8fb; }
      table.data-table { width: 100%; border-collapse: collapse; }
      table.data-table th { background: {{theme.primaryColor}}; color: #fff; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.06em; padding: 7px 9px; text-align: left; }
      table.data-table td { padding: 7px 9px; font-size: 11px; border-bottom: 1px solid #e3e6ec; }
      table.data-table tr:nth-child(even) td { background: #f6f8fb; }
      .number-cell { text-align: right; }
      table.data-table tr.total-row td { font-weight: 700; border-top: 2px solid {{theme.primaryColor}}; background: #fff; }
      .note { margin-top: 22px; padding-top: 8px; border-top: 1px solid #e3e6ec; font-size: 9px; color: #8a8f98; }
    </style>
  </head>
  <body>
    <div class="band">
      <table>
        <tr>
          <td style="width: 45%;">
            {{#if theme.logoUrl}}<img src="{{theme.logoUrl}}" alt="Logo" />{{/if}}
            <div class="company">{{theme.companyName}}</div>
          </td>
          <td class="contact" style="width: 55%;">
            {{#if theme.address}}{{theme.address}}<br />{{/if}}
            {{#if theme.email}}{{theme.email}}{{/if}}{{#if theme.phone}} &nbsp;|&nbsp; {{theme.phone}}{{/if}}
          </td>
        </tr>
      </table>
    </div>
    <div class="accent"></div>

    <div class="content">
      <h1 class="title">{{report.displayName}}</h1>
      <div class="subtitle">Period: {{params.dateFrom}} — {{params.dateTo}}</div>

      <table class="cards">
        <tr>
          <td><div class="card-label">Reference</div><div class="card-value">{{params.reference}}</div></td>
          <td class="gap"><div class="card-label">Prepared for</div><div class="card-value">{{params.recipientName}}</div></td>
          <td class="gap"><div class="card-label">Generated</div><div class="card-value">{{params.printedAt}}</div></td>
        </tr>
      </table>

      <div class="section-title">Transactions</div>
      <!-- Map your list to a variable on the Field mapping tab, then replace "items" below. -->
      <table class="data-table">
        <thead>
          <tr><th>Date</th><th>Description</th><th class="number-cell">Amount</th></tr>
        </thead>
        <tbody>
          {{#each items}}
          <tr>
            <td>{{this.date}}</td>
            <td>{{this.description}}</td>
            <td class="number-cell">{{currency this.amount}}</td>
          </tr>
          {{/each}}
          <tr class="total-row"><td colspan="2">Total</td><td class="number-cell">{{currency total}}</td></tr>
        </tbody>
      </table>

      <div class="note">
        Printed by {{params.preparedBy}}. This statement is system generated and does not require a signature.
      </div>
    </div>
  </body>
</html>
`,
  },
  {
    id: "sacco-member-statement",
    name: "SACCO member statement — deposits & shares",
    description: "Landscape statement built from the snippets: letterhead, statement banner, member details, and a grouped deposits/shares summary matrix with a grand total. Comes with sample data.",
    layout: "logo-left-right",
    sampleData: SACCO_SAMPLE_DATA,
    html: `<!doctype html>
<html>
  <head>
    <style>
      @page { size: A4 landscape; margin: 8mm 10mm 12mm; @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 8px; color: #8a8f98; } }
      body { font-family: "Calibri", Arial, sans-serif; font-size: 9px; color: #1a1a1a; margin: 0; }
      .rf-matrix tbody tr:nth-child(even) td { background: #f6f8fb; }
    </style>
  </head>
  <body>
${snippetHtml("logo-header-split")}
${STATEMENT_BANNER}
${MEMBER_DETAILS_GRID}
${SECTION_HEADING}
${SUMMARY_MATRIX}
${snippetHtml("footer-block")}
    <div style="text-align: center; font-size: 8px; color: #8a8f98; margin-top: 8px;">
      This statement is system generated and does not require a signature.
    </div>
  </body>
</html>
`,
  },
  {
    id: "logo-left-right",
    name: "Statement — logo left & right",
    description: "Classic letterhead: your logo on the left, company details centered, a second logo slot on the right.",
    layout: "logo-left-right",
    html: `<!doctype html>
<html>
  <head>
    <style>
      body { font-family: "Calibri", Arial, sans-serif; font-size: 12px; color: #1a1a1a; margin: 0; }
      .header-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      .header-table td { vertical-align: middle; padding: 4px; }
      .header-table img { max-width: 90px; max-height: 90px; object-fit: contain; }
      .company-name { text-align: center; font-size: 16px; font-weight: 700; color: {{theme.primaryColor}}; margin: 0 0 4px; }
      .company-contact { text-align: center; font-size: 10.5px; color: #444; line-height: 1.5; }
      .report-title { text-align: center; font-size: 14px; font-weight: 700; margin: 10px 0 4px; border-bottom: 2px solid {{theme.primaryColor}}; padding-bottom: 6px; }
      .report-meta { text-align: center; font-size: 10.5px; color: #666; margin-bottom: 10px; }${SHARED_TABLE_STYLES}
      .footer-table { width: 100%; border-collapse: collapse; margin-top: 24px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 9.5px; color: #555; }
      .footer-table td { vertical-align: top; padding: 4px; }
      .footer-note { text-align: center; font-size: 8.5px; color: #999; margin-top: 10px; }
    </style>
  </head>
  <body>
    <table class="header-table">
      <tr>
        <td style="width: 20%; text-align: left;">
          {{#if theme.logoUrl}}<img src="{{theme.logoUrl}}" alt="Logo" />{{/if}}
        </td>
        <td style="width: 60%;">
          <div class="company-name">{{theme.companyName}}</div>
          <div class="company-contact">
            {{#if theme.email}}Email: {{theme.email}}{{/if}}{{#if theme.phone}} &nbsp;|&nbsp; Tel: {{theme.phone}}{{/if}}<br />
            {{#if theme.address}}{{theme.address}}{{/if}}
          </div>
        </td>
        <td style="width: 20%; text-align: right;">
          {{#if theme.logoUrl}}<img src="{{theme.logoUrl}}" alt="Logo" />{{/if}}
        </td>
      </tr>
    </table>

    <div class="report-title">{{report.displayName}}</div>
    <div class="report-meta">Generated for {{params.dateFrom}} — {{params.dateTo}}</div>

    <div class="summary-box">
      <strong>Reference:</strong> {{params.reference}} &nbsp;|&nbsp;
      <strong>Prepared for:</strong> {{params.recipientName}}
    </div>

    <div class="section-title">Statement details</div>
    <table class="data-table">
      <thead>
        <tr><th>Description</th><th>Date</th><th style="text-align:right;">Amount</th></tr>
      </thead>
      <tbody>
        <tr><td>{{data.description}}</td><td>{{data.date}}</td><td style="text-align:right;">{{currency data.amount}}</td></tr>
      </tbody>
    </table>

    <table class="footer-table">
      <tr>
        <td style="width: 50%;">
          Printed by: {{params.preparedBy}}<br />
          Date printed: {{params.printedAt}}
        </td>
        <td style="width: 50%; text-align: right;">
          {{theme.companyName}}<br />
          {{theme.address}}<br />
          {{#if theme.email}}{{theme.email}}{{/if}}{{#if theme.phone}} &nbsp;|&nbsp; {{theme.phone}}{{/if}}
        </td>
      </tr>
    </table>
    <div class="footer-note">This statement is system generated and does not require a signature.</div>
  </body>
</html>
`,
  },
  {
    id: "logo-centered",
    name: "Statement — centered logo",
    description: "A single centered logo above the company name and contact details — clean, formal masthead.",
    layout: "logo-centered",
    html: `<!doctype html>
<html>
  <head>
    <style>
      body { font-family: "Calibri", Arial, sans-serif; font-size: 12px; color: #1a1a1a; margin: 0; }
      .header { text-align: center; margin-bottom: 14px; }
      .header img { max-width: 130px; max-height: 90px; object-fit: contain; margin-bottom: 6px; }
      .company-name { font-size: 17px; font-weight: 700; color: {{theme.primaryColor}}; margin: 2px 0; }
      .company-contact { font-size: 10.5px; color: #444; line-height: 1.6; }
      .report-title { text-align: center; font-size: 14px; font-weight: 700; margin: 12px 0 4px; padding-bottom: 8px; border-bottom: 2px solid {{theme.primaryColor}}; }
      .report-meta { text-align: center; font-size: 10.5px; color: #666; margin-bottom: 10px; }${SHARED_TABLE_STYLES}
      .footer { text-align: center; margin-top: 24px; border-top: 1px solid #ddd; padding-top: 8px; font-size: 9.5px; color: #777; }
    </style>
  </head>
  <body>
    <div class="header">
      {{#if theme.logoUrl}}<img src="{{theme.logoUrl}}" alt="Logo" /><br />{{/if}}
      <div class="company-name">{{theme.companyName}}</div>
      <div class="company-contact">
        {{#if theme.address}}{{theme.address}}<br />{{/if}}
        {{#if theme.email}}Email: {{theme.email}}{{/if}}{{#if theme.phone}} &nbsp;|&nbsp; Tel: {{theme.phone}}{{/if}}
      </div>
    </div>

    <div class="report-title">{{report.displayName}}</div>
    <div class="report-meta">Generated for {{params.dateFrom}} — {{params.dateTo}}</div>

    <div class="summary-box">
      <strong>Reference:</strong> {{params.reference}} &nbsp;|&nbsp;
      <strong>Prepared for:</strong> {{params.recipientName}}
    </div>

    <div class="section-title">Statement details</div>
    <table class="data-table">
      <thead>
        <tr><th>Description</th><th>Date</th><th style="text-align:right;">Amount</th></tr>
      </thead>
      <tbody>
        <tr><td>{{data.description}}</td><td>{{data.date}}</td><td style="text-align:right;">{{currency data.amount}}</td></tr>
      </tbody>
    </table>

    <div class="footer">
      {{theme.companyName}} — this statement is system generated and does not require a signature.
    </div>
  </body>
</html>
`,
  },
  {
    id: "minimal",
    name: "Minimal — no letterhead",
    description: "Just the report title and a clean data table, no logo or contact block — good for internal/ops reports.",
    layout: "minimal",
    html: `<!doctype html>
<html>
  <head>
    <style>
      body { font-family: "Calibri", Arial, sans-serif; font-size: 12px; color: #1a1a1a; margin: 0; }
      .report-title { font-size: 15px; font-weight: 700; margin: 0 0 4px; color: {{theme.primaryColor}}; }
      .report-meta { font-size: 10.5px; color: #666; margin-bottom: 12px; }${SHARED_TABLE_STYLES}
      .footer { margin-top: 20px; border-top: 1px solid #ddd; padding-top: 6px; font-size: 9px; color: #999; }
    </style>
  </head>
  <body>
    <div class="report-title">{{report.displayName}}</div>
    <div class="report-meta">Generated for {{params.dateFrom}} — {{params.dateTo}}</div>

    <table class="data-table">
      <thead>
        <tr><th>Description</th><th>Date</th><th style="text-align:right;">Amount</th></tr>
      </thead>
      <tbody>
        <tr><td>{{data.description}}</td><td>{{data.date}}</td><td style="text-align:right;">{{currency data.amount}}</td></tr>
      </tbody>
    </table>

    <div class="footer">Generated by {{theme.companyName}}.</div>
  </body>
</html>
`,
  },
  {
    id: "listing",
    name: "Listing — grouped transaction table",
    description:
      "A repeating table driven by array data (e.g. contributions, a cash book) — one section per group, one row per record. Good starting point if you're mapping a list/array field.",
    layout: "listing",
    html: `<!doctype html>
<html>
  <head>
    <style>
      body { font-family: "Calibri", Arial, sans-serif; font-size: 11px; color: #1a1a1a; margin: 0; }
      .report-title { font-size: 15px; font-weight: 700; margin: 0 0 4px; color: {{theme.primaryColor}}; }
      .report-meta { font-size: 10.5px; color: #666; margin-bottom: 12px; }${SHARED_TABLE_STYLES}
      table.data-table td.number-cell { text-align: right; }
      .footer { margin-top: 20px; border-top: 1px solid #ddd; padding-top: 6px; font-size: 9px; color: #999; }
    </style>
  </head>
  <body>
    <div class="report-title">{{report.displayName}}</div>
    <div class="report-meta">Generated for {{params.dateFrom}} — {{params.dateTo}}</div>

    {{!--
      Coming from Thymeleaf? Handlebars works the same way, different spelling:
        th:each="x : \${list}"        ->  {{#each list}} ... {{/each}}   (current item is "this")
        th:text="\${x.field}"         ->  {{this.field}}  (or just {{field}} inside the loop)
        th:class="\${x.odd ? 'a':'b'}" -> not needed - CSS ":nth-child(even)" in SHARED_TABLE_STYLES
                                          above already stripes every other row automatically.
        #numbers.formatDecimal(x,1,'COMMA',2,'POINT') -> {{currency this.amount}} or {{number this.amount 2}}

      "groups" below is whatever target variable name you gave an array field on the Field
      mapping tab (e.g. map a source field like "$.cashBook" or "$.contributions" to "groups").
      Each group is expected to look like { "groupName": "...", "transactions": [ {...}, ... ] } -
      rename the paths below to match your own field mapping.
    --}}
    {{#each groups}}
      <div class="section-title">{{this.groupName}}</div>
      <table class="data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Receipt No</th>
            <th>Customer</th>
            <th>Particulars</th>
            <th>Category</th>
            <th>Currency</th>
            <th>Payment mode</th>
            <th>Prepared by</th>
            <th>Certified by</th>
            <th>Approved by</th>
            <th style="text-align:right;">Amount</th>
          </tr>
        </thead>
        <tbody>
          {{#each this.transactions}}
          <tr>
            <td>{{dateFormat this.transDate "dd/MM/yyyy"}}</td>
            <td>{{this.receiptNo}}</td>
            <td>{{this.customer}}</td>
            <td>{{this.particulars}}</td>
            <td>{{this.category}}</td>
            <td>{{this.currency}}</td>
            <td>{{this.paymentMode}}</td>
            <td>{{this.preparer}}</td>
            <td>{{this.certifier}}</td>
            <td>{{this.approver}}</td>
            <td class="number-cell">{{currency this.amount}}</td>
          </tr>
          {{/each}}
        </tbody>
      </table>
    {{/each}}

    <div class="footer">Generated by {{theme.companyName}}.</div>
  </body>
</html>
`,
  },
];
